import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DEFAULT_SDS_UI_PICTOGRAMS, SdsApiService, SdsUiPictogramOption } from '../../../safety-data-sheet/services/sds-api.service';
import { SdsRecord } from '../../../safety-data-sheet/models/sds.model';

/** GHS pictogram render info. */
interface PictogramInfo {
  label: string;
  ghs: string;
  svg: string;
}

/** Aggregate risk counts across all records, for the Low/Medium/High bars. */
interface RiskSummary {
  low: number;
  medium: number;
  high: number;
}

/** Compact per-chemical view model rendered by the dashboard box. */
interface SdsWidgetItem {
  id: number;
  product: string;
  riskValue: number;
  riskText: string;
  riskColor: string;
  pictograms: string[];
}

/** Number of rows shown before the user expands the box. */
const INITIAL_VISIBLE_COUNT = 5;

/** Pictograms shown inline per row; the rest collapse into a "+N" chip so the risk bars keep their width. */
const MAX_VISIBLE_PICTOGRAMS = 3;

@Component({
  selector: 'app-chemicals-sds-widget',
  templateUrl: './chemicals-sds-widget.component.html',
  styleUrls: ['./chemicals-sds-widget.component.css'],
  standalone: false
})
export class ChemicalsSdsWidgetComponent implements OnInit {
  @Input() businessId: number;

  items: SdsWidgetItem[] = [];
  loading = true;
  expanded = false;

  /** Card has two alternate views; the risk graph is the default. */
  view: 'graph' | 'list' = 'graph';

  /** GHS pictogram id → render info; seeded with defaults, refined from the backend. */
  private pictogramMap: Record<string, PictogramInfo> =
    this.buildPictogramMap(DEFAULT_SDS_UI_PICTOGRAMS);

  constructor(
    private sdsApiService: SdsApiService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.loadPictograms();
    this.loadData();
  }

  /** Aggregate risk counts across all records (drives the Low/Medium/High bars). */
  get riskSummary(): RiskSummary {
    const summary: RiskSummary = { low: 0, medium: 0, high: 0 };
    for (const item of this.items) {
      switch ((item.riskText || '').toLowerCase()) {
        case 'low':
          summary.low++;
          break;
        case 'medium':
          summary.medium++;
          break;
        case 'high':
          summary.high++;
          break;
      }
    }
    return summary;
  }

  /** Largest bucket (min 1) so the bars scale relative to the biggest one. */
  get riskSummaryMax(): number {
    const s = this.riskSummary;
    return Math.max(1, s.low, s.medium, s.high);
  }

  riskBarPercent(count: number): number {
    return Math.round((count / this.riskSummaryMax) * 100);
  }

  getPictogramInfo(id: string): PictogramInfo {
    return this.pictogramMap[id] || { label: id, ghs: '?', svg: '' };
  }

  /** First few pictograms of a row; the remainder is summarised by the "+N" chip. */
  visiblePictograms(item: SdsWidgetItem): string[] {
    return item.pictograms.slice(0, MAX_VISIBLE_PICTOGRAMS);
  }

  hiddenPictogramCount(item: SdsWidgetItem): number {
    return Math.max(0, item.pictograms.length - MAX_VISIBLE_PICTOGRAMS);
  }

  /** Tooltip for the "+N" chip: the GHS names of the pictograms it hides. */
  hiddenPictogramNames(item: SdsWidgetItem): string {
    return item.pictograms
      .slice(MAX_VISIBLE_PICTOGRAMS)
      .map(id => this.getPictogramInfo(id).ghs)
      .join(', ');
  }

  private loadPictograms(): void {
    this.sdsApiService.getPictogramOptions().subscribe({
      next: options => {
        if (options?.length) {
          this.pictogramMap = this.buildPictogramMap(options);
        }
      },
      // Non-fatal: the default map already provides the standard GHS icons.
      error: () => { /* keep defaults */ }
    });
  }

  private buildPictogramMap(options: SdsUiPictogramOption[]): Record<string, PictogramInfo> {
    return (options || []).reduce((map, option) => {
      map[option.id] = { label: option.label, ghs: option.ghs, svg: option.svg };
      return map;
    }, {} as Record<string, PictogramInfo>);
  }

  /** Rows currently visible: the first five when collapsed, all when expanded. */
  get visibleItems(): SdsWidgetItem[] {
    return this.expanded ? this.items : this.items.slice(0, INITIAL_VISIBLE_COUNT);
  }

  get hasMore(): boolean {
    return this.items.length > INITIAL_VISIBLE_COUNT;
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  toggleView(): void {
    this.view = this.view === 'graph' ? 'list' : 'graph';
  }

  /** Icon for the header toggle: it advertises the view it switches TO. */
  get viewToggleIcon(): string {
    return this.view === 'graph' ? 'pi-list' : 'pi-chart-bar';
  }

  get viewToggleLabelKey(): string {
    return this.view === 'graph' ? 'SDS_WIDGET_VIEW_LIST' : 'SDS_WIDGET_VIEW_GRAPH';
  }

  openDetail(item: SdsWidgetItem): void {
    this.router.navigate(['../safety-data-sheets/detail', item.id], { relativeTo: this.route });
  }

  openRiskList(risk: 'low' | 'medium' | 'high'): void {
    this.router.navigate(['../safety-data-sheets/list'], {
      relativeTo: this.route,
      queryParams: { risk }
    });
  }

  private loadData(): void {
    this.loading = true;

    this.sdsApiService
      .getBusinessDocumentRecords(this.businessId)
      .pipe(catchError(() => of([] as SdsRecord[])))
      .subscribe(records => {
        this.items = (records || [])
          .map(record => this.toWidgetItem(record))
          .sort((left, right) =>
            right.riskValue - left.riskValue ||
            left.product.localeCompare(right.product));

        this.loading = false;
      });
  }

  private toWidgetItem(record: SdsRecord): SdsWidgetItem {
    return {
      id: record.Id,
      product: record.ProductName,
      riskValue: record.RiskFigure?.Value ?? 0,
      riskText: record.RiskFigure?.ValueText ?? '',
      riskColor: record.RiskFigure?.BackColor ?? '#9e9e9e',
      pictograms: record.Pictograms || []
    };
  }
}
